// Intelligent Redirection Guard
// Catches unmatched (404) URLs, predicts the most relevant page via keyword
// matching and redirects the visitor there with a friendly rebrand toast.
// True 301 status for crawlers is handled at the edge via public/_redirects;
// this component handles the human UX (instant client-side redirect + toast).

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

// Priority-ordered keyword map (top-to-bottom wins on conflicts).
const REDIRECT_RULES = [
  { keywords: ["yoga", "classes", "practice"], to: "/yoga-classes" },
  { keywords: ["reiki", "healing", "treatment", "massage", "chakra"], to: "/treatments" },
  { keywords: ["retreat", "workshop", "event"], to: "/retreats" },
  { keywords: ["about", "story", "annelie"], to: "/about" },
];

const REBRAND_MESSAGE =
  "Welcome! I have rebranded from Seventh Sense Healing to Annelie Lily. I've redirected you to the most relevant page on my new site.";

const predictDestination = (pathname) => {
  const path = (pathname || "").toLowerCase();
  for (const rule of REDIRECT_RULES) {
    if (rule.keywords.some((kw) => path.includes(kw))) return rule.to;
  }
  return "/"; // catch-all -> homepage
};

const RedirectionGuard = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const destination = predictDestination(pathname);
    // Defer toast to the next tick so the root <Toaster /> has subscribed to
    // sonner's store before the toast is emitted (avoids a cold-load race that
    // drops toasts fired during the initial mount).
    const toastTimer = setTimeout(() => {
      toast(REBRAND_MESSAGE, {
        duration: 5000,
        id: "rebrand-redirect",
      });
      navigate(destination, { replace: true });
    }, 0);
    return () => clearTimeout(toastTimer);
  }, [pathname, navigate]);

  return null;
};

export default RedirectionGuard;
